<template>
  <div class="limiter-content pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <!-- Cabecalho no padrao das telas da area do procurador -->
    <header class="ag-header">
      <span class="ag-eyebrow"><v-icon small color="primary">mdi-briefcase-account-outline</v-icon> ÁREA DO PROCURADOR</span>
      <h1 class="ag-title">Assembleia <span>do caso</span></h1>
      <p class="ag-subtitle">Credenciamentos, sessões, sala virtual e votações dos credores que você representa.</p>
    </header>

    <login-required-notice v-if="!user" />

    <template v-else>
    <!-- Seletor de caso: casos em que o procurador tem associacao aprovada -->
    <div class="ag-field">
      <v-autocomplete v-model="selectedClientId" :items="cases" item-value="client_id" item-text="client_name"
        label="Selecione o caso" outlined dense hide-details :loading="loadingCases"
        no-data-text="Nenhum caso com associação aprovada" clearable @change="onCaseChange"></v-autocomplete>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="darkSecondary" class="mt-8"></v-progress-linear>
    <div v-else-if="error" class="ag-notice ag-notice--danger mt-8">
      <v-icon color="#b42318">mdi-alert-circle-outline</v-icon>
      <div>{{ error }}</div>
    </div>

    <div v-else-if="!data" class="ag-empty">
      <div class="ag-empty__icon"><v-icon size="34" color="darkSecondary">mdi-account-group-outline</v-icon></div>
      <div class="ag-empty__title">Selecione um caso</div>
      <div class="ag-empty__text">Aparecem aqui os casos em que você tem procuração aprovada.</div>
    </div>

    <template v-else>
      <!-- Titulo: nome da AGC + caso -->
      <section class="ag-summary">
        <div class="ag-summary__icon"><v-icon color="darkSecondary">mdi-gavel</v-icon></div>
        <div class="ag-summary__main">
          <div class="ag-summary__title">{{ data.title }}</div>
          <div class="ag-muted">Processo nº {{ data.client.process_number || '-' }} · Procurador: {{ data.procurator.name }}</div>
        </div>
      </section>

      <!-- Credenciamentos do caso: credor, CPF/CNPJ, classe e valor -->
      <section class="ag-card">
        <div class="ag-card__head ag-card__head--row">
          <div>
            <h2>Credores representados neste caso</h2>
            <p>Credenciamento e presença referem-se à sessão "{{ currentSessionLabel }}".</p>
          </div>
          <v-btn depressed color="primary" class="ag-btn" :disabled="!substitutionSelection.length" @click="requestSubstitutionForSelected">
            <v-icon left small>mdi-account-switch-outline</v-icon>
            {{ substitutionButtonLabel }}
          </v-btn>
        </div>

        <div class="table-scroll">
          <v-simple-table dense class="ag-table agc-case-table">
            <thead>
              <tr>
                <th class="text-left">Credor</th>
                <th class="text-left">CPF/CNPJ</th>
                <th class="text-left">Classe</th>
                <th class="text-right">Valor do crédito</th>
                <th class="text-left">Credenciamento</th>
                <th class="text-left">Presença</th>
                <th class="text-left agc-case-table__select">
                  <v-checkbox :input-value="allSubstitutableSelected" :indeterminate="someSubstitutableSelected" :disabled="!substitutableCreditorIds.length"
                    dense hide-details color="darkSecondary" class="mt-0 pt-0 d-inline-flex" title="Marcar todos os credores que podem ser substabelecidos" @change="toggleAllSubstitution" />
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="creditor in data.creditors">
                <tr v-for="(credit, index) in (creditor.credits.length ? creditor.credits : [null])" :key="`${creditor.creditor_user_id}-${credit ? credit.user_creditor_id : 'x'}`"
                  :class="{ 'ag-table__continuation': index > 0 }">
                  <td class="ag-strong ag-capitalize">{{ index === 0 ? creditor.creditor_name : '' }}</td>
                  <td>{{ index === 0 ? creditor.creditor_registration : '' }}</td>
                  <td>{{ credit ? credit.class_name : 'Sem crédito no QGC' }}</td>
                  <td class="text-right ag-strong ag-nowrap">{{ credit ? formatAmount(credit.amount, credit.currency) : '-' }}</td>
                  <td>
                    <template v-if="index === 0">
                      <span class="ag-pill" :class="`ag-pill--${accreditationTone(currentAccreditation(creditor))}`">
                        <span class="ag-pill__dot"></span>{{ accreditationLabel(currentAccreditation(creditor)) }}
                      </span>
                      <div v-if="currentSubstitution(creditor)" class="mt-1">
                        <button type="button" class="ag-pill ag-pill--button" :class="currentSubstitution(creditor).state === 'approved' ? 'ag-pill--info' : 'ag-pill--warn'"
                          :title="`Protocolo ${currentSubstitution(creditor).protocol || '-'} · ${currentSubstitution(creditor).new_procurator_name} · clique para ver o pedido`"
                          @click="openSubstitution(currentSubstitution(creditor))">
                          <v-icon x-small>mdi-account-switch-outline</v-icon>
                          Substab. {{ substitutionLabel(currentSubstitution(creditor).status) }}
                        </button>
                      </div>
                    </template>
                  </td>
                  <td>
                    <template v-if="index === 0">
                      <!-- Presenca so depois que a sessao foi aberta (a API manda null antes) -->
                      <span v-if="currentAccreditation(creditor) && Number(currentAccreditation(creditor).status) === 1 && currentSessionCell(creditor).present !== null" class="ag-pill"
                        :class="currentSessionCell(creditor).present ? 'ag-pill--ok' : 'ag-pill--muted'">
                        <span class="ag-pill__dot"></span>{{ currentSessionCell(creditor).present ? 'Presente' : 'Sem presença' }}
                      </span>
                      <span v-else class="ag-muted">—</span>
                      <div v-if="currentSessionCell(creditor) && currentSessionCell(creditor).represented_by_me === false" class="ag-muted mt-1">
                        por {{ currentSessionCell(creditor).current_procurator }}
                      </div>
                    </template>
                  </td>
                  <td class="agc-case-table__select">
                    <v-checkbox v-if="index === 0 && canSubstitute(creditor)" v-model="substitutionSelection" :value="creditor.creditor_user_id"
                      dense hide-details color="darkSecondary" class="mt-0 pt-0" />
                  </td>
                </tr>
              </template>
            </tbody>
          </v-simple-table>
        </div>
        <p class="ag-hint">
          Com procuração geral, marque todos os credores: é criado um pedido para cada um, com o mesmo novo procurador e os mesmos documentos.
          Em prosseguimento de assembleia já instalada, o substabelecimento é a única forma de trocar o procurador.
        </p>
      </section>

      <!-- Dados da assembleia atual: periodo de credenciamento, ingresso e sala virtual -->
      <section v-if="data.current_assembly" class="ag-card">
        <div class="ag-card__head">
          <h2>{{ data.current_assembly.convocation_label }} · {{ data.current_assembly.root_name }}</h2>
          <span v-if="data.current_assembly.installed_in" class="ag-pill ag-pill--ok mt-2">
            <span class="ag-pill__dot"></span>Instalada na sessão "{{ data.current_assembly.installed_in.label }}"
          </span>
        </div>
        <div v-for="session in data.current_assembly.sessions" :key="session.general_meeting_id" class="ag-session">
          <div class="ag-session__head">
            <strong>{{ session.session_label }}</strong>
            <span class="ag-muted">{{ session.date | defaultDate('DD/MM/YYYY HH:mm', false, true) }} BRT</span>
            <span class="ag-pill" :class="`ag-pill--${statusTone(session.status)}`"><span class="ag-pill__dot"></span>{{ statusLabel(session.status) }}</span>
            <v-spacer></v-spacer>
            <!-- Lista de presencas publica: liberada no inicio da sessao -->
            <v-btn v-if="session.presence_list_published_at" text rounded small color="darkSecondary" class="ag-action"
              :href="presenceListUrl(session.general_meeting_id)" target="_blank" rel="noopener"
              :title="'Publicada em ' + $options.filters.defaultDate(session.presence_list_published_at, 'DD/MM/YYYY HH:mm') + ' BRT'">
              <v-icon left small>mdi-file-document-outline</v-icon> Lista de presença (PDF)
            </v-btn>
            <v-btn v-if="session.zoom_enabled && Number(session.status) === 5" depressed color="primary" class="ag-btn" :loading="zoomLoadingId === session.general_meeting_id" @click="joinZoom(session)">
              <v-icon left small>mdi-video-outline</v-icon> Entrar na sala virtual
            </v-btn>
          </div>
          <div class="ag-session__grid">
            <div>
              <div class="ag-label">Credenciamento</div>
              <div class="ag-text">{{ accreditationPeriod(session) }}</div>
              <div class="ag-small" :class="session.accreditation_availability && session.accreditation_availability.is_open ? 'ag-small--ok' : 'ag-muted'">
                {{ session.accreditation_availability ? session.accreditation_availability.message : '' }}
              </div>
            </div>
            <div>
              <div class="ag-label">Ingresso</div>
              <div class="ag-small">
                Apenas credores com credenciamento aprovado e presença concedida participam e votam.
                <template v-if="session.zoom_enabled">A sala virtual abre quando a sessão estiver em andamento; o link é individual, gerado ao clicar em "Entrar na sala virtual".</template>
                <template v-else>Sessão sem sala virtual configurada.</template>
              </div>
            </div>
            <div>
              <div class="ag-label">Sala virtual</div>
              <div class="ag-text">{{ zoomStatusLabel(session) }}</div>
            </div>
          </div>
          <div v-if="session.description" class="ag-small mt-2">{{ session.description }}</div>
        </div>
      </section>
      <div v-else class="ag-notice">
        <v-icon color="darkSecondary">mdi-information-outline</v-icon>
        <div>Nenhuma assembleia cadastrada para este caso.</div>
      </div>

      <!-- Historicos e votacoes: titulos sempre visiveis, conteudo abre e fecha.
           "Votacoes em andamento" ja vem aberto quando ha votacao rolando. -->
      <v-expansion-panels v-model="openSections" multiple flat class="ag-sections mb-10">
        <v-expansion-panel v-if="data.history.length" class="ag-section">
          <v-expansion-panel-header>
            <span class="ag-section__title">Histórico de convocações e prosseguimentos <span class="ag-count">{{ data.history.length }}</span></span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-for="group in data.history" :key="group.root_id" class="mb-5">
              <div class="ag-group-title">
                {{ group.convocation_label }} · {{ group.root_date | defaultDate('DD/MM/YYYY', false, true) }}
                <span class="ag-muted">{{ group.root_name }}</span>
              </div>
              <div class="table-scroll">
                <v-simple-table dense class="ag-table">
                  <thead>
                    <tr>
                      <th class="text-left">Credor</th>
                      <th v-for="session in group.sessions" :key="session.general_meeting_id" class="text-left">
                        {{ session.session_label }}<br>
                        <span class="ag-th-sub">{{ session.date | defaultDate('DD/MM/YYYY', false, true) }} · {{ statusLabel(session.status) }}</span>
                        <span v-if="session.installed_here" class="ag-th-sub ag-small--ok"> · instalada</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="creditor in group.creditors" :key="creditor.creditor_user_id">
                      <td class="ag-strong ag-capitalize">{{ creditor.creditor_name }}</td>
                      <td v-for="cell in creditor.sessions" :key="cell.general_meeting_id">
                        <div class="ag-cell">
                          <span v-if="cell.continuation_blocked" class="ag-pill ag-pill--danger"><span class="ag-pill__dot"></span>Sem direito (ausente antes)</span>
                          <span v-else class="ag-pill" :class="`ag-pill--${accreditationTone(cell.accreditation)}`">
                            <span class="ag-pill__dot"></span>{{ accreditationLabel(cell.accreditation) }}
                          </span>
                          <span v-if="cell.accreditation && Number(cell.accreditation.status) === 1 && cell.present !== null" class="ag-pill" :class="cell.present ? 'ag-pill--ok' : 'ag-pill--muted'">
                            <span class="ag-pill__dot"></span>{{ cell.present ? 'Presente' : 'Sem presença' }}
                          </span>
                          <span class="ag-small">Procurador: <strong>{{ cell.current_procurator }}</strong></span>
                          <span v-for="sr in cell.substitutions" :key="sr.id" class="ag-small" :class="substitutionClass(sr.status)">
                            Substab. {{ sr.new_procurator_name }} · {{ substitutionLabel(sr.status) }}<span v-if="sr.valid_until"> até {{ sr.valid_until | defaultDate('DD/MM/YYYY') }}</span>
                          </span>
                          <v-btn v-if="cell.can_request_substitution" x-small text color="darkSecondary" class="ag-action px-0" @click="goToSubstitution(cell.accreditation.id)">Solicitar substabelecimento</v-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="ag-section">
          <v-expansion-panel-header>
            <span class="ag-section__title">Votações em andamento <span class="ag-count" :class="{ 'ag-count--live': data.running_votings.length }">{{ data.running_votings.length }}</span></span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p v-if="!data.running_votings.length" class="ag-muted mb-0">Nenhuma votação em andamento neste caso.</p>
            <div v-for="voting in data.running_votings" :key="voting.id" class="ag-session">
              <div class="ag-session__head">
                <strong>{{ voting.name }}</strong>
                <span class="ag-muted">{{ voting.session_label }}</span>
              </div>
              <div v-for="creditor in voting.creditors" :key="`${voting.id}-${creditor.creditor_user_id}`">
                <div v-for="credit in creditor.credits" :key="`${voting.id}-${credit.user_creditor_id}`" class="ag-vote-row">
                  <span class="ag-strong ag-capitalize">{{ creditor.creditor_name }}</span>
                  <span class="ag-muted">{{ credit.class_name }}</span>
                  <span class="ag-pill" :class="`ag-pill--${voteTone(credit.vote)}`"><span class="ag-pill__dot"></span>{{ credit.vote_label }}</span>
                  <v-btn depressed small color="primary" class="ag-btn ag-btn--small" @click="goToVoting(voting, creditor)">{{ credit.vote ? 'Ver / alterar voto' : 'Votar' }}</v-btn>
                </div>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="ag-section">
          <v-expansion-panel-header>
            <span class="ag-section__title">Histórico de votações finalizadas <span class="ag-count">{{ data.finished_votings.length }}</span></span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p v-if="!data.finished_votings.length" class="ag-muted mb-0">Nenhuma votação finalizada neste caso.</p>
            <!-- Cada votacao abre e fecha; o cabecalho ja mostra o resultado. -->
            <v-expansion-panels v-else multiple accordion flat class="ag-votings">
              <v-expansion-panel v-for="voting in data.finished_votings" :key="voting.id">
                <v-expansion-panel-header>
                  <div class="ag-session__head">
                    <strong>{{ voting.name }}</strong>
                    <span class="ag-muted">{{ voting.session_label }}</span>
                    <span v-if="voting.result" class="ag-pill" :class="`ag-pill--${resultTone(voting.result)}`"><span class="ag-pill__dot"></span>Resultado: {{ voting.result }}</span>
                    <span v-else class="ag-pill ag-pill--muted"><span class="ag-pill__dot"></span>{{ Number(voting.status) === 5 ? 'Resultado publicado' : 'Aguardando publicação do resultado' }}</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="creditor in voting.creditors" :key="`${voting.id}-${creditor.creditor_user_id}`">
                    <div v-for="credit in creditor.credits" :key="`${voting.id}-${credit.user_creditor_id}`" class="ag-vote-row">
                      <span class="ag-strong ag-capitalize">{{ creditor.creditor_name }}</span>
                      <span class="ag-muted">{{ credit.class_name }}</span>
                      <span class="ag-pill" :class="`ag-pill--${voteTone(credit.vote)}`"><span class="ag-pill__dot"></span>{{ credit.vote_label }}</span>
                      <span class="ag-muted">{{ credit.voted_at ? $options.filters.defaultDate(credit.voted_at, 'DD/MM/YYYY HH:mm') : '' }}</span>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
    </template>

    <whats-button></whats-button>
    <top-button></top-button>
  </div>
</template>


<script>
import axios from 'axios';
import topButton from '@/components/layout/GoToTopButton.vue';
import whatsButton from '@/components/layout/WhatsAppButton.vue';

export default {
  name: 'ProcuratorCaseAgc',

  components: { topButton, whatsButton },

  data: () => ({
    user: null,
    loading: false,
    loadingCases: false,
    error: null,
    data: null,
    cases: [],
    selectedClientId: null,
    zoomLoadingId: null,
    // creditor_user_id dos credores marcados para substabelecer.
    substitutionSelection: [],
    // Paineis abertos dos historicos (indices do v-expansion-panels).
    openSections: [],
  }),

  computed: {
    substitutableCreditorIds() {
      if (!this.data) return [];
      return this.data.creditors.filter((creditor) => this.canSubstitute(creditor)).map((creditor) => creditor.creditor_user_id);
    },
    allSubstitutableSelected() {
      return this.substitutableCreditorIds.length > 0 && this.substitutableCreditorIds.every((id) => this.substitutionSelection.includes(id));
    },
    substitutionButtonLabel() {
      const count = this.substitutionSelection.length;
      if (!count) return 'Substabelecer selecionados';
      return count === 1 ? 'Substabelecer 1 selecionado' : `Substabelecer ${count} selecionados`;
    },
    someSubstitutableSelected() {
      return this.substitutionSelection.length > 0 && !this.allSubstitutableSelected;
    },
    // Sessao mais recente da assembleia atual (a coluna de credenciamento se refere a ela).
    currentSessionLabel() {
      const group = this.data && this.data.history[0];
      if (!group || !group.sessions.length) return 'sessão atual';
      return group.sessions[group.sessions.length - 1].session_label;
    },
  },

  watch: {
    // Navegacao entre /procurador/casos/:id/assembleia e a rota do menu sem recriar a pagina.
    '$route.params.clientId'(value) {
      if (value && Number(value) !== Number(this.selectedClientId)) {
        this.selectedClientId = Number(value);
        this.load();
      }
    },
  },

  created() {
    this.user = this.$root.user;
    if (!this.user) return;
    if (Number(this.user.type) !== 2) { this.$router.push('/resumo-geral'); return; }
    this.selectedClientId = Number(this.$route.params.clientId || this.$route.query.client_id) || null;
    this.loadCases();
    if (this.selectedClientId) this.load();
  },

  methods: {
    // PDF publico da lista de presencas (rota sem login na API).
    presenceListUrl(meetingId) {
      const base = String(axios.defaults.baseURL || '').replace(/\/+$/, '');
      return `${base}/api/general-meetings/${meetingId}/presence-list`;
    },

    // Casos com associacao aprovada, sem repetir.
    async loadCases() {
      this.loadingCases = true;
      try {
        const result = await axios.get('/api/procurators/creditors', { params: { page: 1, limit: 1000, search: { status: 1 } } });
        const items = (result.data.content && (result.data.content.data || result.data.content)) || [];
        const seen = {};
        this.cases = items
          .filter((item) => Number(item.status) === 1 && item.client_id && !seen[item.client_id] && (seen[item.client_id] = true))
          .map((item) => ({ client_id: item.client_id, client_name: item.client ? item.client.name : (item.client_name || `Caso ${item.client_id}`) }))
          .sort((a, b) => String(a.client_name).localeCompare(String(b.client_name)));
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 4000 });
      } finally {
        this.loadingCases = false;
      }
    },

    onCaseChange() {
      this.data = null;
      if (!this.selectedClientId) return;
      // Mantem o caso na URL para o link poder ser compartilhado/recarregado.
      if (!this.$route.params.clientId && String(this.$route.query.client_id || '') !== String(this.selectedClientId)) {
        this.$router.replace({ path: this.$route.path, query: { client_id: this.selectedClientId } });
      }
      this.load();
    },

    async load() {
      if (!this.selectedClientId) return;
      this.loading = true;
      this.error = null;
      try {
        const result = await axios.get(`/api/procurators/cases/${this.selectedClientId}/agc`);
        this.data = result.data.content;
        this.substitutionSelection = [];
        // Com votacao em andamento, o painel dela ja abre (indice muda se nao houver historico).
        const runningIndex = this.data.history.length ? 1 : 0;
        this.openSections = this.data.running_votings.length ? [runningIndex] : [];
      } catch (error) {
        this.data = null;
        this.error = this.$codes(error);
      } finally {
        this.loading = false;
      }
    },

    // Credenciamento do credor na sessao mais recente da assembleia atual.
    currentAccreditation(creditor) {
      const cell = this.currentSessionCell(creditor);
      return cell ? cell.accreditation : null;
    },

    // Substabelecimento vigente ou em andamento na sessao atual.
    currentSubstitution(creditor) {
      const cell = this.currentSessionCell(creditor);
      return cell ? cell.substitution || null : null;
    },

    currentSessionCell(creditor) {
      const group = this.data && this.data.history[0];
      if (!group) return null;
      const row = group.creditors.find((c) => Number(c.creditor_user_id) === Number(creditor.creditor_user_id));
      if (!row || !row.sessions.length) return null;
      return row.sessions[row.sessions.length - 1];
    },

    openSubstitution(substitution) {
      this.$router.push({ path: '/substabelecimentos', query: { id: substitution.id } });
    },

    canSubstitute(creditor) {
      const group = this.data && this.data.history[0];
      if (!group) return false;
      const row = group.creditors.find((c) => Number(c.creditor_user_id) === Number(creditor.creditor_user_id));
      return !!(row && row.sessions.some((s) => s.can_request_substitution));
    },

    // Credenciamento (da sessao mais recente que aceita pedido) de cada credor.
    substitutableAccreditationId(creditorUserId) {
      const group = this.data && this.data.history[0];
      const row = group && group.creditors.find((c) => Number(c.creditor_user_id) === Number(creditorUserId));
      const cell = row && row.sessions.slice().reverse().find((s) => s.can_request_substitution);
      return cell ? cell.accreditation.id : null;
    },

    toggleAllSubstitution() {
      this.substitutionSelection = this.allSubstitutableSelected ? [] : [...this.substitutableCreditorIds];
    },

    // Abre o formulario ja com os credenciamentos dos credores marcados.
    requestSubstitutionForSelected() {
      const ids = this.substitutionSelection.map((id) => this.substitutableAccreditationId(id)).filter(Boolean);
      if (!ids.length) return;
      this.$router.push({ path: '/substabelecimentos', query: { accreditation_ids: ids.join(',') } });
    },

    goToSubstitution(accreditationId) {
      this.$router.push({ path: '/substabelecimentos', query: { accreditation_id: accreditationId } });
    },

    goToVoting(voting, creditor) {
      this.$router.push({ path: `/assembleia-credores/votacao/${voting.id}`, query: { creditor_user_id: creditor.creditor_user_id } });
    },

    async joinZoom(session) {
      // O link e individual por credor representado: usa o primeiro com credenciamento aprovado nesta sessao.
      const group = this.data.history.find((g) => g.sessions.some((s) => Number(s.general_meeting_id) === Number(session.general_meeting_id)));
      const creditor = group && group.creditors.find((c) => c.sessions.some((s) => Number(s.general_meeting_id) === Number(session.general_meeting_id) && s.accreditation && Number(s.accreditation.status) === 1 && !s.continuation_blocked));
      if (!creditor) {
        this.$dialog.notify.error('Nenhum credor representado com credenciamento aprovado nesta sessão.', { position: 'top-right', timeout: 5000 });
        return;
      }
      this.zoomLoadingId = session.general_meeting_id;
      try {
        const result = await axios.post(`/api/general-meetings/${session.general_meeting_id}/zoom/join`, { creditor_user_id: creditor.creditor_user_id });
        const joinUrl = result.data.content && result.data.content.join_url;
        if (!joinUrl) throw new Error('Link individual da reunião não disponível.');
        window.open(joinUrl, '_blank');
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error) || error.message, { position: 'top-right', timeout: 5000 });
      } finally {
        this.zoomLoadingId = null;
      }
    },

    accreditationPeriod(session) {
      if (session.accreditation_enabled === false) return 'Credenciamento não habilitado';
      const start = session.accreditation_starts_at ? `${this.$options.filters.defaultDate(session.accreditation_starts_at, 'DD/MM/YYYY HH:mm', false, true)} BRT` : 'imediato';
      const end = session.accreditation_ends_at ? `${this.$options.filters.defaultDate(session.accreditation_ends_at, 'DD/MM/YYYY HH:mm', false, true)} BRT` : 'sem encerramento programado';
      return `De ${start} até ${end}`;
    },

    zoomStatusLabel(session) {
      if (!session.zoom_enabled) return 'Não configurada';
      return { scheduled: 'Agendada', waiting: 'Aguardando início', started: 'Em andamento', ended: 'Encerrada' }[session.zoom_status] || (session.zoom_status || 'Configurada');
    },

    accreditationLabel(accreditation) {
      if (!accreditation) return 'Não credenciado';
      return { 0: 'Credenciamento pendente', 1: 'Credenciado', 2: 'Credenciamento rejeitado' }[Number(accreditation.status)] || 'Credenciamento';
    },

    // Tons das pilulas de status (mesmas faixas das cores anteriores).
    accreditationTone(accreditation) {
      if (!accreditation) return 'muted';
      return { 0: 'warn', 1: 'ok', 2: 'danger' }[Number(accreditation.status)] || 'muted';
    },

    statusLabel(status) {
      return { 0: 'Aberta', 1: 'Fechada', 2: 'Interrompida', 3: 'Suspensa', 4: 'Finalizada', 5: 'Em andamento' }[Number(status)] || '-';
    },

    statusTone(status) {
      return { 0: 'info', 1: 'muted', 2: 'danger', 3: 'warn', 4: 'ok', 5: 'info' }[Number(status)] || 'muted';
    },

    substitutionLabel(status) {
      return { draft: 'rascunho', submitted: 'em análise', under_review: 'em análise', waiting_documents: 'aguardando documentos', waiting_registration: 'aguardando cadastro', approved: 'aprovado', rejected: 'rejeitado', cancelled: 'cancelado' }[status] || status;
    },

    substitutionClass(status) {
      return { approved: 'ag-small--ok', rejected: 'red--text' }[status] || 'ag-muted';
    },

    resultTone(result) {
      return { Aprovado: 'ok', Rejeitado: 'danger', Empate: 'warn' }[result] || 'muted';
    },

    voteTone(vote) {
      return { 1: 'ok', 2: 'danger', 3: 'muted' }[Number(vote)] || 'warn';
    },

    formatAmount(amount, currency) {
      const symbol = Number(currency) === 2 ? 'US$' : (Number(currency) === 3 ? '€' : 'R$');
      return `${symbol} ${Number(amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Mesmo padrao visual de "Meus credores", "Minha conta" e "Resumo de casos". */

.ag-header {
  padding: 32px 0 24px;
}

.ag-eyebrow {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #766386;
}

.ag-title {
  margin: 14px 0 10px;
  font-size: clamp(28px, 3.4vw, 40px);
  font-weight: 700;
  color: #25113c;
  letter-spacing: -0.035em;
  line-height: 1.2;
}

.ag-title span {
  color: #806697;
}

.ag-subtitle {
  margin: 0;
  color: #73677c;
  font-size: 14px;
}

.ag-muted {
  font-size: 13px;
  color: rgba(24, 0, 50, 0.55);
}

.ag-strong {
  font-weight: 600;
  color: #180032;
}

.ag-capitalize {
  text-transform: capitalize;
}

.ag-small {
  font-size: 12px;
  color: #4a3a5c;
}

.ag-small--ok {
  color: #1e7b4f !important;
}

.ag-text {
  font-size: 14px;
  color: #25113c;
}

.ag-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8b769d;
  margin-bottom: 2px;
}

/* Seletor de caso com contorno lilas */
.ag-field {
  max-width: 520px;
  margin-bottom: 28px;
}

.ag-field ::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(126, 85, 171, 0.45);
  border-radius: 10px;
}

.ag-field ::v-deep .v-text-field--outlined:hover fieldset {
  border-color: #7e55ab;
}

.ag-field ::v-deep .v-input__slot {
  border-radius: 10px !important;
}

/* Avisos e estado vazio */
.ag-notice {
  display: flex;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 14px;
  background: rgba(120, 119, 229, 0.09);
  color: #2a1a40;
  font-size: 14px;
  margin-bottom: 16px;
}

.ag-notice--danger {
  background: #fdecea;
  color: #7a1b12;
}

.ag-empty {
  text-align: center;
  padding: 40px 16px 56px;
}

.ag-empty__icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(120, 119, 229, 0.12);
}

.ag-empty__title {
  font-size: 17px;
  font-weight: 600;
  color: #180032;
}

.ag-empty__text {
  margin-top: 6px;
  color: rgba(24, 0, 50, 0.6);
  font-size: 14px;
}

/* Resumo da AGC */
.ag-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  border-radius: 16px;
  background: #faf9fd;
  border: 1px solid rgba(24, 0, 50, 0.06);
  margin-bottom: 16px;
}

.ag-summary__icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(120, 119, 229, 0.14);
  flex-shrink: 0;
}

.ag-summary__main {
  min-width: 0;
}

.ag-summary__title {
  font-size: 18px;
  font-weight: 700;
  color: #180032;
  letter-spacing: -0.01em;
}

/* Cartoes de secao */
.ag-card {
  padding: 22px 22px 18px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(24, 0, 50, 0.07);
  margin-bottom: 16px;
}

.ag-card__head {
  margin-bottom: 14px;
}

.ag-card__head--row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
}

.ag-card__head h2 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7e55ab;
}

.ag-card__head p {
  margin: 4px 0 0;
  font-size: 13px;
  color: rgba(24, 0, 50, 0.55);
}

.ag-hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: rgba(24, 0, 50, 0.55);
}

/* Botoes */
.ag-btn {
  height: 40px !important;
  padding: 0 18px !important;
  border-radius: 10px;
  text-transform: none;
  letter-spacing: 0;
  font-size: 14px;
  font-weight: 600;
}

.ag-btn--small {
  height: 30px !important;
  padding: 0 12px !important;
  font-size: 13px;
}

.ag-action {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
}

/* Tabelas leves */
.ag-table ::v-deep table {
  border-collapse: separate;
}

.ag-table ::v-deep th {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8b769d !important;
  border-bottom: 1px solid rgba(24, 0, 50, 0.08) !important;
  vertical-align: bottom;
  padding-top: 6px !important;
  padding-bottom: 8px !important;
}

.ag-table ::v-deep td {
  font-size: 13px !important;
  color: #25113c;
  border-bottom: 1px solid rgba(24, 0, 50, 0.05) !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  vertical-align: middle;
}

.ag-table ::v-deep tbody tr:hover {
  background: #faf9fd !important;
}

.ag-th-sub {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: rgba(24, 0, 50, 0.5);
}

.agc-case-table__select {
  white-space: nowrap;
  width: 1%;
}

.ag-nowrap {
  white-space: nowrap;
}

.ag-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.table-scroll {
  overflow-x: auto;
}

/* Pilulas de status */
.ag-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  border: 0;
}

.ag-pill .v-icon {
  color: inherit;
}

.ag-pill__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.ag-pill--button {
  cursor: pointer;
}

.ag-pill--button:hover {
  filter: brightness(0.96);
}

.ag-pill--ok {
  background: #e7f6ee;
  color: #1e7b4f;
}

.ag-pill--info {
  background: rgba(120, 119, 229, 0.14);
  color: #4b3a8f;
}

.ag-pill--warn {
  background: #fff4dc;
  color: #a16207;
}

.ag-pill--danger {
  background: #fdecea;
  color: #b42318;
}

.ag-pill--muted {
  background: #f1f0f4;
  color: #6b6475;
}

/* Sessoes */
.ag-session {
  padding: 14px 16px;
  border-radius: 12px;
  background: #faf9fd;
  margin-bottom: 10px;
}

.ag-session__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  color: #180032;
}

.ag-session__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 12px;
}

.ag-vote-row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid rgba(24, 0, 50, 0.06);
  font-size: 13px;
}

.ag-vote-row > .ag-pill {
  justify-self: start;
}

.ag-vote-row > .v-btn {
  justify-self: end;
}

/* Paineis de historico */
.ag-sections ::v-deep .ag-section {
  border: 1px solid rgba(24, 0, 50, 0.07);
  border-radius: 16px !important;
  background: #fff;
}

.ag-sections ::v-deep .ag-section + .ag-section {
  margin-top: 12px;
}

.ag-sections ::v-deep .ag-section::before,
.ag-votings ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}

.ag-sections ::v-deep .ag-section::after {
  border-top: 0 !important;
}

.ag-sections ::v-deep .ag-section > .v-expansion-panel-header {
  min-height: 60px;
  padding: 16px 22px;
}

.ag-section__title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7e55ab;
}

.ag-count {
  display: inline-block;
  min-width: 22px;
  margin-left: 6px;
  padding: 0 7px;
  border-radius: 11px;
  background: #f1f0f4;
  color: #4a3a5c;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 20px;
  text-align: center;
}

.ag-count--live {
  background: #e7f6ee;
  color: #1e7b4f;
}

.ag-group-title {
  font-weight: 700;
  color: #180032;
  margin-bottom: 6px;
}

.ag-votings ::v-deep .v-expansion-panel {
  border-radius: 12px !important;
  background: #faf9fd;
  margin-bottom: 8px;
}

.ag-votings ::v-deep .v-expansion-panel::after {
  border-top: 0 !important;
}

.ag-votings ::v-deep .v-expansion-panel-header {
  min-height: 48px;
  padding: 10px 16px;
}

/* Celular */
@media (max-width: 760px) {
  .ag-session__grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .ag-vote-row {
    grid-template-columns: minmax(0, 1fr) auto;
  }
}
</style>
